import {
  acceptWaitinglistEntryViaApi,
  acceptVisitWishReservationViaApi,
  abandonVisitWishReservationViaApi,
  allocationErrorCode,
  usesAllocationApi,
} from 'utils/reservationWrites';
import { trackAnalytics, analyticsErrorCategory } from "utils/analytics";
import { AlertState } from 'Components/Organisms/Alert';
import { Severity } from 'Contexts/RestaurantContext';
import { SignatureContext } from 'Contexts/SignatureContext';
import {
  RequestV03Document,
  WaitingListEntryDocument,
} from 'types/shared/documents';
import { RequestStatus } from 'types/shared/helper/reservations';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useEditSessionState } from 'Contexts/reservationEditSession';
import cloneDeep from 'lodash/cloneDeep';
import {
  EditReservation,
  Reservation,
  ReservationStatus,
  ResSettings,
  TimeSlot,
} from 'types/reservations';
import { db } from 'utils/firebase';
import { customDataWrites } from 'utils/customData';
import {
  EDITOR_FIELDS,
  Placement,
  PlacementOutdated,
  ReservationChanged,
  WaitingListEntryChanged,
  advanceDayCounters,
  capacityProtocolOn,
  deletePlaceholderIfUntouched,
  finalizeWaitingListReservation,
  saveEditedReservation,
  updateReservation,
  writeReservationDocument,
} from 'utils/reservationWrites';
import { actingAs } from 'utils/reservationAuthor';
import { hasConcreteTime } from 'utils/waitinglistPresentation';
import {
  createRandomId,
  hasBackendOwnedHold,
  timeHelper,
  timeToBlock,
} from 'utils/helper';
import { PLACEHOLDER_GUEST_NAME } from 'utils/placeholderReservation';
import server from 'utils/server';
import {
  requestProtection,
  validateStaffProtection,
  waiveProtection,
} from 'App/NoShowProtection/api';
import {
  apiErrorMessage,
  staffProtectionChoice,
  staffProtectionError,
  WAIVED_AT_CREATION_NOTE,
} from 'App/NoShowProtection/domain';
import useToast from './useToast';
import useDateNow from './useDateNow';
import { useRouter } from './useRouter';
import useRestaurant from './useRestaurant';
import useAlert from './useAlert';
import {
  CouponChangeDecision,
  confirmCouponChange,
  reviewCouponChange,
} from 'App/Coupons/CouponChange/couponChange';
import usePinAuth from './usePinAuth';
import { SecurityGrant, trackChange } from 'utils/securityActivity/grant';
import { reservationChanges } from 'utils/securityActivity/events';
import {
  getTicketPartialPaymentFraction,
  getPaidAddOnMinimumGuests,
  getTicketPayableAmount,
  getTicketPaymentAmount,
  getTicketPaymentSummary,
  getTicketSelectionError,
  getTicketValidTillMinutes,
  normalizeTicketSelection,
} from 'utils/ticketSession';

export type ReservationSettings = {
  pendingTill?: number | undefined;
  standardFloorPlan?: string | undefined;
  maxGuestsOnWebsite?: number | undefined;
  hideResLengthInEmails?: boolean | undefined;
  phoneNumberMandatory?: boolean | undefined;
  addUserDetails?: boolean | undefined;
  signatureRequired?: boolean | undefined;
  customAttr?: string[] | undefined;
  minTimeInAdvance?: number | undefined;
  emailMandatory?: boolean;
  shuffleMode?: boolean;
  blocksBetweenReservations?: number;
};

const EMPTY_GUEST = {
  name: PLACEHOLDER_GUEST_NAME,
  email: '',
  phone: '',
  comment: '',
  preferredLanguage: 'de',
  sendEmail: false,
  attr: [],
};

const isTicketSessionMode = (mode?: EditReservation['ticketMode']) =>
  mode === 'pay_at_restaurant' || mode === 'payment_link';

const useEditReservation = (
  restaurant: string | null,
  updatedBy: string | null,
  alert: React.Dispatch<React.SetStateAction<AlertState | null>>,
  currentDate: string,
  setcurrentReservation: React.Dispatch<React.SetStateAction<string | null>>,
  settings?: ReservationSettings,
  isLightReservation?: boolean,
  tableGuestsMap?: Record<string, number>,
  reservationLengthPerSizeMap?: Record<number, number>
) => {
  const { experimentalFlags, apps } = useRestaurant();

  const { validateSignature } = useContext(SignatureContext);

  const { authenticate } = usePinAuth();

  const sendConfirmationEmailDefault =
    !experimentalFlags?.data?.confirmationEmailOffByDefault;

  const [editReservation, seteditReservation, beginEditSession] =
    useEditSessionState<null | EditReservation>(null, `${restaurant}/${currentDate}`);

  // Advances whenever an editor is opened here or any editor is closed, so a
  // callback that completes later (the table swap after its PIN prompt and
  // write) can tell whether the editor it started from is still the open one.
  const editorSessionRef = useRef(0);
  const startEditorSession = () => {
    editorSessionRef.current += 1;
  };
  const editorOpen = !!editReservation;

  useEffect(() => {
    if (!editorOpen) startEditorSession();
  }, [editorOpen]);

  const [signaturePromptOpen, setSignaturePromptOpen] =
    useState<boolean>(false);

  const [originalUrl, setoriginalUrl] = useState<string | null>(null);

  const toast = useToast();

  const DateNow = useDateNow();

  const navigate = useRouter();

  // Safety net for abandoned creates: step 1 writes a placeholder doc, but
  // only the sidebar X-button deletes it. Any other way out (row click, date
  // switch, screen change) leaks the placeholder as a stuck ghost until the
  // day-view cleanup. Watch the create-session id and delete the doc when the
  // session ends without finalize, but never when the guest data was touched.
  const createSessionRef = useRef<string | null>(null);
  const finalizedRef = useRef<string | null>(null);
  const visitWishWriteInFlight = useRef(false);
  const visitWishHold = useRef<{ restaurantId: string; wishId: string; reservationId: string } | null>(null);

  const abandonVisitWishHold = (hold: NonNullable<typeof visitWishHold.current>) =>
    abandonVisitWishReservationViaApi(hold.restaurantId, hold.wishId, hold.reservationId)
      .catch((error) => {
        console.error('Visit wish preparation could not be released', error);
        toast('Die vorbereitete Reservierung konnte nicht freigegeben werden. Bitte prüfen Sie die Anfrage erneut.', Severity.WARNING);
      });

  useEffect(() => {
    const previous = visitWishHold.current;
    const current = restaurant && editReservation?.visitWishId && editReservation.visitWishHoldCreated && editReservation.reservationId
      ? { restaurantId: restaurant, wishId: editReservation.visitWishId, reservationId: editReservation.reservationId }
      : null;
    visitWishHold.current = current;
    if (previous && previous.reservationId !== current?.reservationId && previous.reservationId !== finalizedRef.current) {
      void abandonVisitWishHold(previous);
    }
  }, [restaurant, editReservation?.visitWishId, editReservation?.visitWishHoldCreated, editReservation?.reservationId]);

  useEffect(() => () => {
    const held = visitWishHold.current;
    if (held && held.reservationId !== finalizedRef.current) void abandonVisitWishHold(held);
  }, []);

  useEffect(() => {
    const current =
      editReservation?.new && !editReservation.walkin && !editReservation.visitWishId
        ? editReservation.reservationId ?? null
        : null;
    const previous = createSessionRef.current;

    createSessionRef.current = current;

    if (
      !previous ||
      previous === current ||
      previous === finalizedRef.current ||
      // The same doc reopened as a normal edit (expired hold) is in use, not
      // abandoned.
      previous === editReservation?.reservationId
    )
      return;

    // Transaction, not get-then-delete: a finalize landing between the two
    // would otherwise let us delete a reservation that just got real guest
    // data. The read is revalidated at commit, so the delete only happens
    // while the doc is still an unconfirmed placeholder hold whose whole
    // guest is untouched, not merely a document with the placeholder name.
    deletePlaceholderIfUntouched(
      previous,
      { guest: EMPTY_GUEST },
      (stored) => hasBackendOwnedHold(stored)
    ).catch((err) => {
      // The day-view cleanup can remove the same placeholder first, which
      // makes the delete fail. Log instead of leaving a silent rejection.
      console.error('Placeholder cleanup failed', err);
    });
  }, [
    editReservation?.reservationId,
    editReservation?.new,
    editReservation?.walkin,
  ]);

  const goBackToOriginal = () => {
    if (!originalUrl) return;
    if (!navigate.pathname.endsWith(originalUrl.split('?')[0])) {
      navigate.push(
        `/${restaurant}/reservations/${currentDate}/${originalUrl}`
      );
    }
    setoriginalUrl(null);
  };

  /**
   * Opens the editor for `reservation`, the copy staff see with what they
   * asked for (dropped tables, the assistant's proposal). The save writes only
   * what differs from `stored`, the reservation exactly as stored when the
   * editor opened, and only while those fields still hold there.
   */
  const handleRelocate = (
    reservation: Reservation | null,
    keepTables = reservation?.fixed ?? false,
    stored?: Reservation | null
  ) => {
    // Active holds stay locked (someone is mid-create); expired ones must
    // open for editing, otherwise a stuck placeholder can never be repaired.
    const expiredHold =
      !!reservation?.validTill &&
      reservation.validTill <= DateNow() &&
      !hasBackendOwnedHold(reservation);

    if (reservation?.validTill && !reservation.ticketId && !expiredHold) {
      toast('Diese Reservierung kann nicht bearbeitet werden.', Severity.ERROR);
      return;
    }

    if (reservation === null) {
      seteditReservation(null);
    } else if (stored?.id !== reservation.id) {
      // Without the stored reservation the save could only compare with a
      // shown copy and would miss (or refuse) the very change staff make.
      toast('Die Reservierung konnte nicht geladen werden. Bitte lade die Seite neu.', Severity.ERROR);
    } else {
      let {
        id,
        date,
        time,
        tables = [],
        reservationLength,
        occassion,
        guests,
        guest,
        walkIn,
        tableStr,
        ticket,
        ticketId,
      } = reservation;
      const ticketEvent =
        ticket?.event ?? (reservation.ticketId ? reservation.event ?? null : null);
      const ticketMode = ticket
        ? ticket.payAtRestaurant
          ? 'pay_at_restaurant'
          : 'payment_link'
        : undefined;
      const ticketCouponAmount = getTicketPaymentAmount(ticket, 'coupon');
      const ticketVoucherAmount = getTicketPaymentAmount(ticket, 'voucher');

      let timeSlot = {
        time,
        block: timeToBlock(time),
        reservationLength,
        blockedSlots: 0,
        combinations: [],
        maxGuestsReached: false,
        totalSlots: 0,
      };

      console.log({
        keepTables,
      });

      startEditorSession();
      seteditReservation({
        // Copied when the editor opens: the form's tables may be the very array
        // the availability list later sorts in place.
        baseline: Object.fromEntries(
          EDITOR_FIELDS.map((field) => [field, cloneDeep((stored as any)[field] ?? null)])
        ),
        date,
        time,
        tables,
        tableStr,
        reservationLength,
        reservationId: id,
        occassion,
        guests,
        guest,
        walkin: walkIn,
        timeSlot,
        keepTables,
        // Only heal holds that already carry real guest data. An untouched
        // placeholder must stay expired so the cleanup can still delete it.
        clearValidTill: expiredHold && guest?.name !== EMPTY_GUEST.name,
        sendConfirmationEmail: sendConfirmationEmailDefault && !!guest?.email,
        ticketId: ticketId ?? ticket?.id ?? null,
        event: ticketEvent,
        ticketMode,
        ticketOriginalAmount: ticket ? getTicketPayableAmount(ticket) : undefined,
        ticketOriginalGuests: guests,
        ticketPaidMinGuests: getPaidAddOnMinimumGuests(ticket),
        ticketPaidAddOns: getPaidAddOnMinimumGuests(ticket) > 0 ? ticket?.optionalAddOns : undefined,
        ticketOriginalCouponAmount: ticketCouponAmount,
        ticketOriginalVoucherAmount: ticketVoucherAmount,
        ticketCouponCode: (ticket as any)?.coupon_code ?? undefined,
        ticket: ticket
          ? {
              options: ticket.options ?? {},
              optionalAddOns: ticket.optionalAddOns ?? {},
            }
          : undefined,
        ticketPartialPayment: ticket?.partialPayment ?? 1,
        ticketCanEdit: (ticket as any)?.paymentRequest?.canEdit ?? true,
      });
    }
  };

  // const walkinResLength =

  const handleNewReservation = (
    date: string,
    walkin = false,
    tables: string[] | null = null,
    occassion?: string,
    space?: string
  ) => {
    let time: string | undefined = undefined,
      timeSlot: TimeSlot | null = null,
      guests: number | undefined = undefined,
      reservationLength: number | undefined = undefined;

    if (walkin) {
      time = timeHelper();

      timeSlot = {
        block: timeToBlock(time),
        blockedSlots: 0,
        combinations: [],
        maxGuestsReached: false,
        time,
        reservationLength: 4,
        totalSlots: 0,
      };

      if (!!experimentalFlags?.data?.walkinDefaultOccassion) {
        occassion = experimentalFlags.data.walkinDefaultOccassion;
      }

      const guestMapValue = tableGuestsMap?.[tables?.[0] ?? ''];

      if (guestMapValue) {
        guests = guestMapValue;

        let resLength = reservationLengthPerSizeMap?.[guestMapValue];

        console.log({ resLength, guestMapValue, reservationLengthPerSizeMap });

        if (resLength) {
          timeSlot.reservationLength = resLength;
          reservationLength = resLength;
        }
      } else {
        alert({
          title: 'How many guests are there?',
          description: 'Please enter the number of guests for the reservation',
          descriptionTranslation: 'reservations',
          titleTranslation: 'reservations',
          inputFieldLabel: 'Number of guests',
          inputFieldLabelTranslation: 'reservations',
          onSubmit: (_, value) => {
            startEditorSession();
            seteditReservation({
              date,
              tables,
              guest: null,
              space,
              time,
              occassion,
              walkin,
              timeSlot,
              reservationLength: undefined,
              page: 0,
              new: true,
              guests: Number(value || 2),
              sendConfirmationEmail: sendConfirmationEmailDefault,
              keepTables: walkin,
            });
          },
          actions: [
            {
              id: 'cancel',
              label: 'Cancel',
              onClick: () => {},
            },
            {
              id: 'submit',
              label: 'Next',
              submit: true,
            },
          ],
        });

        return;
      }
    }

    startEditorSession();
    seteditReservation({
      date,
      tables,
      guest: null,
      space,
      time,
      occassion,
      walkin,
      timeSlot,
      reservationLength,
      page: 0,
      new: true,
      guests,
      sendConfirmationEmail: sendConfirmationEmailDefault,
      keepTables: walkin,
    });
  };

  const startStaffNoShowProtection = async (
    reservationId: string,
    phone?: string
  ) => {
    if (!restaurant || !editReservation) return;
    const choice = staffProtectionChoice({
      waive: !!editReservation.noShowProtectionWaive,
      canCharge: !!editReservation.noShowProtectionCanCharge,
      phone,
      sendSms: editReservation.guest?.sendSms,
    });

    try {
      if (choice === 'sms') {
        await requestProtection(restaurant, reservationId, true);
        toast(
          'No-show protection: deposit requested via SMS',
          Severity.SUCCESS,
          'noShowProtection'
        );
      } else if (choice === 'waive') {
        await requestProtection(restaurant, reservationId, false);
        await waiveProtection(restaurant, reservationId, WAIVED_AT_CREATION_NOTE);
      }
    } catch (error: any) {
      const serverMessage = apiErrorMessage(error, '');
      if (serverMessage) toast(serverMessage, Severity.ERROR, null);
      else toast('No-show protection could not be requested.', Severity.ERROR, 'noShowProtection');
    }
  };

  const submitReservation = async (
    sendConfirmationEmail = false,
    signatureOverride?: string,
    grant?: SecurityGrant
  ) => {
    if (!editReservation?.timeSlot) return;
    const submissionStillOpen = beginEditSession();
    trackAnalytics("dashboard_reservation_submitted", {
        flow: "dashboard",
        restaurant_id: restaurant || undefined
    });
    if (editReservation.guests !== editReservation.ticketOriginalGuests &&
        (editReservation.guests ?? 0) < (editReservation.ticketPaidMinGuests ?? 0)) {
      toast('Bitte nutze für bezahlte Zusatzleistungen den bestehenden Änderungs- oder Stornoweg.', Severity.WARNING);
      return;
    }

    let {
      time,
      block: startTimeInBlocks,
      // reservationLength,
    } = editReservation.timeSlot;

    let {
      guests = 0,
      date,
      occassion,
      tables = null,
      reservationId,
      space = null,
      waiterSignature: existingSignature,
      tableStr = null,
      sendNoShowSMS = false,
      reservationLength = editReservation?.timeSlot?.reservationLength ?? 4,
    } = editReservation;

    const waiterSignature = signatureOverride ?? existingSignature;

    // A time taken from the availability list, with the tables it offered, is
    // a checked placement: it commits only while the day is as the list saw it.
    // Walk-ins, kept tables (dropped, fixed, swapped or picked by hand, which
    // sets keepTables) and a full time chosen with the PIN-protected
    // blockedTime right are explicit staff placements.
    const placement: Placement =
      !editReservation.walkin &&
      !editReservation.keepTables &&
      !editReservation.timeSlot?.protectedTime
        ? { checked: true, expectation: editReservation.capacityExpectation }
        : { checked: false };

    /** Shows why a save did not happen and fetches the availability again. */
    const changedMeanwhile = (error: unknown) => {
      if (!(error instanceof PlacementOutdated) && !(error instanceof ReservationChanged)) {
        return false;
      }
      toast(error.message, Severity.WARNING, null);
      seteditReservation((eR) =>
        eR === null
          ? null
          : { ...eR, availabilityRevision: (eR.availabilityRevision ?? 0) + 1 }
      );
      return true;
    };

    const createTicketSession = async (
      finalReservation?: {
        guest?: EditReservation['guest'];
        customData?: EditReservation['customData'];
      },
      couponChangeConfirmed = false
    ) => {
      if (
        !restaurant ||
        !reservationId ||
        !isTicketSessionMode(editReservation.ticketMode)
      ) {
        return;
      }

      const eventId = editReservation.event?.id ?? editReservation.timeSlot?.eventId;

      if (!eventId) {
        throw new Error('Der gewählte Slot hat kein Event mehr.');
      }

      const ticket = editReservation.event
        ? normalizeTicketSelection(
            editReservation.event,
            guests,
            editReservation.ticket,
            editReservation.ticketPaidAddOns
          )
        : editReservation.ticket ?? {
            options: {},
            optionalAddOns: {},
          };
      const ticketSummary = editReservation.event
        ? getTicketPaymentSummary({
            event: editReservation.event,
            guests,
            ticket,
            coupon: editReservation.ticketCoupon,
            voucher: editReservation.ticketVoucher,
            partialPayment: 1,
            includeServiceFee: false,
            preservedAddOns: editReservation.ticketPaidAddOns,
          })
        : null;
      const partialPayment =
        editReservation.ticketMode === 'payment_link' && ticketSummary
          ? getTicketPartialPaymentFraction(
              editReservation,
              ticketSummary.ticketTotal
            )
          : 1;
      const validTillMinutes =
        editReservation.ticketMode === 'payment_link'
          ? getTicketValidTillMinutes(
              editReservation.ticketValidTillValue ?? 2,
              editReservation.ticketValidTillUnit ?? 'hours'
            )
          : 0;

      if (editReservation.ticketId) {
        await server.post(
          `/v03/reservations/${restaurant}/res/${reservationId}/ticketOverview`,
          {
            uid: updatedBy,
            guests,
            ticket,
            canEdit: !!editReservation.ticketCanEdit,
            ...(couponChangeConfirmed ? { couponChangeConfirmed: true } : {}),
          }
        );

        return;
      }

      await server.post(
        `/v03/reservations/${restaurant}/res/${reservationId}/ticketSession`,
        {
          uid: updatedBy,
          mode: editReservation.ticketMode,
          eventId,
          ticket,
          coupon_code: editReservation.ticketCouponCode || null,
          voucher_code: editReservation.ticketVoucherCode || null,
          partialPayment,
          validTillMinutes,
          sendVia:
            editReservation.ticketMode === 'payment_link'
              ? editReservation.ticketSendVia ?? 'email'
              : 'none',
          canEdit: !!editReservation.ticketCanEdit,
          ...(finalReservation?.guest ? { guest: finalReservation.guest } : {}),
          ...('customData' in (finalReservation ?? {})
            ? { customData: finalReservation?.customData ?? {} }
            : {}),
        }
      );
    };

    const validateTicketSession = () => {
      if (!isTicketSessionMode(editReservation.ticketMode)) return true;

      const event = editReservation.event;

      if (!event) {
        alert({
          title: 'Kein Event gefunden',
          description: 'Der gewählte Slot hat kein verbundenes Event mehr.',
          titleTranslation: 'reservations',
          descriptionTranslation: 'reservations',
          actions: [{ id: 'ok', label: 'Okay' }],
          onSubmit: () => {},
        });

        return false;
      }

      const ticket = normalizeTicketSelection(
        event,
        guests,
        editReservation.ticket,
        editReservation.ticketPaidAddOns
      );
      const ticketError = getTicketSelectionError(event, guests, ticket, editReservation.ticketPaidAddOns);

      if (ticketError) {
        alert({
          title: 'Ticket-Auswahl unvollständig',
          description: ticketError,
          titleTranslation: 'reservations',
          descriptionTranslation: 'reservations',
          actions: [{ id: 'ok', label: 'Okay' }],
          onSubmit: () => {},
        });

        return false;
      }

      seteditReservation((current) =>
        !current
          ? null
          : {
              ...current,
              ticket,
            }
      );

      return true;
    };

    if (editReservation?.waitinglistEntry) {
      const {
        guest,
        customId,
        source,
        createdBy,
        createdAt,
        customData = {},
      } = editReservation.waitinglistEntry;

      const reservation: Omit<RequestV03Document, 'updateNote'> = {
        restaurant: restaurant || '',
        guest: {
          ...guest,
          sendEmail: sendConfirmationEmail,
        },
        guests: guests || editReservation.waitinglistEntry.guests,
        date,
        time,
        reservationLength,
        startTimeInBlocks,
        endTimeInBlocks: startTimeInBlocks + reservationLength,
        customId,
        source,
        customData,
        occassion:
          editReservation.occassion ||
          editReservation.waitinglistEntry.occassion,
        space,
        tables,
        createdAt,
        createdBy,
        validTill: 0,
        sendConfirmationEmail,
        status: ReservationStatus.SUCCESS,
        fromWaitinglist: true,
      };

      const ref = db
        .collection('requestsV03')
        .doc(editReservation.reservationId);

      const entryRef = db
        .collection('waitinglistEntries')
        .doc(editReservation.reservationId);

      const request = editReservation.waitinglistEntry;
      const managedJanGroup = request.type === 'largeGroup' &&
        request.submissionVersion === 1 && request.communicationStaged === true &&
        request.receiptQueued === true && request.createdBy === 'guest' &&
        request.widgetContext?.schemaVersion === 1 &&
        typeof request.widgetContext.widgetId === 'string' &&
        typeof request.widgetContext.routingRevision === 'string' &&
        typeof request.widgetContext.environmentId === 'string';

      if (managedJanGroup || await usesAllocationApi(restaurant ?? '')) {
        // One API transaction: the request must still be pending, its
        // reservation is created and the request closed together. The API
        // preserves JAN provenance from the stored request, never the browser.
        const acceptedAt = DateNow();
        const actor = waiterSignature || actingAs(restaurant, updatedBy) || undefined;
        try {
          await acceptWaitinglistEntryViaApi(
            restaurant ?? '',
            editReservation.reservationId as string,
            { ...reservation, updatedBy: actor, updatedAt: acceptedAt, updateNote: { updatedAt: acceptedAt, note: 'Reservierung wurde finalisiert' } },
            actor
          );
        } catch (error) {
          const code = allocationErrorCode(error);
          if (code === 'WAITINGLIST_ENTRY_CHANGED') {
            seteditReservation(null);
            return toast('This request was changed in the meantime.', Severity.INFO, 'reservations');
          }
          if (code === 'WAITINGLIST_RESERVATION_EXISTS') {
            return toast('A Reservation allready exists for this Waitinglist entry.', Severity.ERROR, 'reservations');
          }
          throw error;
        }
        seteditReservation(null);
        return;
      }
      // A hold until the finalize below turns it into the booking.
      const placeholder = {
        ...reservation,
        validTill: Date.now() + 15 * 60 * 1000,
        guest: EMPTY_GUEST,
      };

      // The request can be canceled or accepted elsewhere while the form is
      // open, so its status is checked in the same transaction as the create.
      const blocked = await db.runTransaction(async (tx) => {
        const entryDoc = await tx.get(entryRef);
        const reservationDoc = await tx.get(ref);
        const protocolOn = await capacityProtocolOn(restaurant || '', tx);

        if (entryDoc.data()?.status !== RequestStatus.PENDING) return 'changed';

        if (reservationDoc.exists) return 'exists';

        tx.set(ref, placeholder);
        // Staff place the waiting-list guest on purpose: an explicit placement.
        if (protocolOn) advanceDayCounters(tx, restaurant || '', [date]);

        return null;
      });

      if (blocked === 'changed') {
        seteditReservation(null);

        return toast(
          'This request was changed in the meantime.',
          Severity.INFO,
          'reservations'
        );
      }

      if (blocked === 'exists')
        return toast(
          'A Reservation allready exists for this Waitinglist entry.',
          Severity.ERROR,
          'reservations'
        );

      const updatedAt = DateNow();
      const finalizedBy = waiterSignature || actingAs(restaurant, updatedBy) || undefined;

      // One transaction on the stored placeholder and request, so a request
      // never stays open next to its reservation and nothing changed meanwhile
      // is overwritten; it stamps the first confirmation (reservationWrites.ts).
      try {
        await finalizeWaitingListReservation({
          reservationId: ref.id,
          entryId: entryRef.id,
          placeholder,
          confirm: {
            guest: reservation.guest,
            validTill: 0,
            updatedBy: finalizedBy,
            updatedAt,
            updateNote: {
              updatedAt,
              note: 'Reservierung wurde finalisiert',
            },
          },
          entryPatch: { status: 'success', updatedAt, updatedBy: finalizedBy },
        });
      } catch (error) {
        // A leftover placeholder would block every retry with "exists"; only
        // this flow's own untouched placeholder goes, never a booking someone
        // changed or confirmed in the meantime.
        await deletePlaceholderIfUntouched(ref.id, {
          guest: EMPTY_GUEST,
          written: placeholder,
        }).catch((err) => {
          console.error('Placeholder cleanup failed', err);
        });

        if (error instanceof WaitingListEntryChanged) {
          seteditReservation(null);

          return toast(error.message, Severity.INFO, 'reservations');
        }

        if (changedMeanwhile(error)) return;

        throw error;
      }

      trackAnalytics("dashboard_reservation_saved", {
          flow: "dashboard",
          restaurant_id: restaurant || undefined,
          outcome: "created"
      });
      seteditReservation(null);

      return;
    }

    if (editReservation?.new) {
      // New Reservation
      if (editReservation.walkin) {
        // @TODO: Load Settings Walkin Name Required

        reservationLength =
          editReservation.reservationLength ?? reservationLength;

        await writeReservationDocument({
          started: startTimeInBlocks,
          startTimeInBlocks,
          endTimeInBlocks: startTimeInBlocks + reservationLength,
          tables: tables || [],
          tableStr,
          time,
          reservationLength,
          date,
          guests,
          occassion,
          space,
          status: 'success',
          restaurant,
          guest: {
            name: `Walkin ${tables?.join(', ')}`,
            phone: '',
            email: '',
            hostComment: editReservation?.guest?.hostComment ?? '',
            comment: '',
            attr: [],
          },
          customId: createRandomId(),
          source: 'inHouse',
          validTill: 0,
          createdAt: DateNow(),
          createdBy: waiterSignature || actingAs(restaurant, updatedBy),
          walkIn: true,
        });

        trackAnalytics("dashboard_reservation_saved", {
            flow: "dashboard",
            restaurant_id: restaurant || undefined,
            outcome: "walkin"
        });
        seteditReservation(null);
      } else if (editReservation.page === 0) {
        if (!validateTicketSession()) return;

        let customId = createRandomId();

        if (editReservation.addToWaitinglist) {
          alert({
            title: 'Adding Guest to Waitinglist',
            description:
              'By Continueing you are placing the guest on the Waitinglist. No Reservation will be made. Make sure this has been communicated with the guest clearly.',
            descriptionTranslation: 'reservations',
            titleTranslation: 'reservations',
            actions: [
              { id: 'cancel', label: 'Cancel', onClick: () => {} },
              {
                id: 'submit',
                label: 'Continue',
                variant: 'primary',
                onClick: () => {
                  seteditReservation((eR) =>
                    eR === null ? null : { ...eR, page: 1, customId }
                  );
                },
              },
            ],
            onSubmit: () => {
              seteditReservation((eR) =>
                eR === null ? null : { ...eR, page: 1, customId }
              );
            },
          });

          return;
        }

        // @TODO: Pending Till

        let data = {
          startTimeInBlocks,
          endTimeInBlocks: startTimeInBlocks + reservationLength,
          tables,
          tableStr,
          time,
          reservationLength,
          date,
          guests,
          occassion,
          space,
          status: 'success',
          customId,
          restaurant,
          guest: editReservation.visitWishId ? editReservation.guest! : EMPTY_GUEST,
          source: 'inHouse',
          validTill: DateNow() + 300000,
          createdAt: DateNow(),
          createdBy: waiterSignature || actingAs(restaurant, updatedBy),
        };

        const wishId = editReservation.visitWishId;
        const stillOpen = beginEditSession();
        if (wishId && visitWishWriteInFlight.current) return;
        if (wishId) visitWishWriteInFlight.current = true;
        try {
          if (wishId && editReservation.visitWishHoldCreated) {
            const { createdAt, createdBy, source, validTill, status, ...selection } = data;
            const applied = await updateReservation(reservationId!, selection, undefined, placement);
            if (applied === false) throw new ReservationChanged();
          } else {
            reservationId = wishId
              ? await acceptVisitWishReservationViaApi(restaurant!, wishId, reservationId!, data, placement)
              : await writeReservationDocument(data, reservationId || undefined, placement);
          }
        } catch (error) {
          if (changedMeanwhile(error)) return;
          if (wishId) {
            toast(apiErrorMessage(error, 'Die Anfrage konnte nicht übernommen werden. Bitte prüfen Sie die Angaben erneut.'), Severity.ERROR);
            return;
          }
          throw error;
        } finally {
          if (wishId) visitWishWriteInFlight.current = false;
        }

        if (wishId && !stillOpen()) {
          if (editReservation.visitWishHoldCreated !== true) {
            await abandonVisitWishHold({ restaurantId: restaurant!, wishId, reservationId: reservationId! });
          }
          return;
        }

        seteditReservation((eR) =>
          eR === null ? null : { ...eR, reservationId, page: 1, customId, ...(wishId ? { visitWishHoldCreated: true } : {}) }
        );
      } else {
        if (!validateTicketSession()) return;

        let { guest, customData = {} } = editReservation;

        if (!guest || !guest.name) {
          // No TimeSlot Message
          alert({
            title: 'No Guest Information was entered!',
            titleTranslation: 'reservations',
            description: "You can't continue without the guest informations.",
            descriptionTranslation: 'reservations',
            onSubmit: () => {},
            actions: [
              {
                id: 'ok',
                label: 'Okay',
              },
            ],
          });

          return;
        }

        if (settings?.phoneNumberMandatory && guest.phone.length < 4) {
          alert({
            title: 'No Phone Number has been entered',
            titleTranslation: 'reservations',
            description: "You can't continue without the phone number.",
            descriptionTranslation: 'reservations',
            onSubmit: () => {},
            actions: [
              {
                id: 'ok',
                label: 'Okay',
              },
            ],
          });

          return;
        }

        if (settings?.emailMandatory && guest.email.length < 4) {
          alert({
            title: 'No Email entered',
            titleTranslation: 'reservations',
            description: 'You can not continue without an email.',
            descriptionTranslation: 'reservations',
            onSubmit: () => {},
            actions: [
              {
                id: 'ok',
                label: 'Okay',
              },
            ],
          });

          return;
        }

        if (settings?.signatureRequired && !waiterSignature) {
          alert({
            title: 'No Signature has been entered',
            titleTranslation: 'reservations',
            description: "You can't continue without the signature",
            descriptionTranslation: 'reservations',
            onSubmit: () => {},
            actions: [
              {
                id: 'ok',
                label: 'Okay',
              },
            ],
          });

          return;
        }

        if (
          apps.includes('noShowMollie') &&
          !editReservation.addToWaitinglist &&
          !isTicketSessionMode(editReservation.ticketMode)
        ) {
          const template = editReservation.noShowProtectionTemplate;
          const protectionError =
            template === undefined
              ? 'Wait until no-show protection has loaded successfully.'
              : template &&
                staffProtectionError({
                  waive: !!editReservation.noShowProtectionWaive,
                  canCharge: !!editReservation.noShowProtectionCanCharge,
                  phone: guest.phone,
                  sendSms: guest.sendSms,
                });
          if (protectionError) {
            toast(protectionError, Severity.ERROR, 'noShowProtection');
            document
              .getElementById('staff-no-show-protection')
              ?.scrollIntoView({ block: 'nearest' });
            return;
          }
          if (restaurant && reservationId) {
            try {
              await validateStaffProtection(restaurant, reservationId, {
                phone: guest.phone,
                sendSms: guest.sendSms,
                waive: !!editReservation.noShowProtectionWaive,
              });
            } catch (error) {
              const message = apiErrorMessage(
                error,
                'No-show protection could not be requested.'
              );
              seteditReservation((old) =>
                !old ? old : { ...old, noShowProtectionValidationError: message }
              );
              toast(message, Severity.ERROR, 'noShowProtection');
              document
                .getElementById('staff-no-show-protection')
                ?.scrollIntoView({ block: 'nearest' });
              return;
            }
          }
        }

        if (guest?.id?.includes(`-${restaurant}`)) {
          guest.id = guest.id.replace(`-${restaurant}`, '');
        }

        // Mark before the writes, so a finalize still in flight is never
        // mistaken for an abandoned session.
        finalizedRef.current = reservationId ?? null;

        if (editReservation.addToWaitinglist) {
          let {
            time,
            flexibleTime = null,
            preferredFormOfContact = 'call',
          } = editReservation.addToWaitinglist;

          let waitinglistEntry: Omit<WaitingListEntryDocument, 'id'> = {
            date,
            restaurant: restaurant || '',
            guests,
            time,
            flexibleTime,
            preferredFormOfContact,
            guest,
            occassion: occassion || '',
            source: 'inHouse',
            customId: editReservation.customId,
            type: 'waitinglist',
            status: RequestStatus.PENDING,
            createdAt: DateNow(),
            customData,
            createdBy: waiterSignature || actingAs(restaurant, updatedBy) || '',
          };

          await db.collection('waitinglistEntries').add(waitinglistEntry);
        } else if (isTicketSessionMode(editReservation.ticketMode)) {
          await createTicketSession({ guest, customData });
        } else if (sendNoShowSMS) {
          const applied = await updateReservation(reservationId || '', {
            guest,
            customData,
            updatedBy: waiterSignature || actingAs(restaurant, updatedBy),
            updatedAt: DateNow(),
            validTill: 0,
            updateNote: {
              updatedAt: DateNow(),
              note: 'No Show SMS wurde versendet',
            },
            noShowConfirmation: {
              validTill: DateNow() + 60 * 60000,
              sendVia: 'sms',
            },
          });
          if (applied === false) throw new ReservationChanged();
        } else {
          const applied = await updateReservation(reservationId || '', {
            guest,
            customData,
            updatedBy: waiterSignature || actingAs(restaurant, updatedBy),
            updatedAt: DateNow(),
            updateNote: {
              updatedAt: DateNow(),
              note: 'Reservierung wurde finalisiert',
            },
            validTill: 0,
          });
          if (applied === false) throw new ReservationChanged();
        }

        if (
          reservationId &&
          editReservation.noShowProtectionTemplate &&
          !editReservation.addToWaitinglist &&
          !isTicketSessionMode(editReservation.ticketMode)
        ) {
          void startStaffNoShowProtection(reservationId, guest.phone);
        }

        trackAnalytics("dashboard_reservation_saved", {
            flow: "dashboard",
            restaurant_id: restaurant || undefined,
            outcome: editReservation.addToWaitinglist
                ? "waitinglist"
                : editReservation.ticketMode === "payment_link" || sendNoShowSMS
                ? "payment_pending"
                : "created"
        });
        if (!editReservation.visitWishId || submissionStillOpen()) seteditReservation(null);
      }
    } else {
      if (!validateTicketSession()) return;

      // A booking that redeemed a coupon under the coupon-first contract is
      // re-priced against its promised terms: staff see old and new amounts
      // and decide about goodwill before anything is saved (Q23).
      let couponDecision: CouponChangeDecision | null = null;
      if (editReservation.ticketId && restaurant && reservationId) {
        try {
          const review = await reviewCouponChange(restaurant, reservationId, {
            guests,
            date,
            time,
            occassion,
            ticket: editReservation.event
              ? normalizeTicketSelection(editReservation.event, guests, editReservation.ticket)
              : editReservation.ticket ?? undefined,
          });
          if (review === 'cancelled') return;
          couponDecision = review;
        } catch (error: any) {
          // An api without the coupon route (older deployment) has nothing to check.
          const routeMissing =
            error?.response?.status === 404 &&
            String(error?.response?.data?.message ?? '').includes('Not Found -');
          if (routeMissing) couponDecision = null;
          else {
          alert({
            title: 'Discount code could not be checked',
            description: 'The booking was not saved. Please try again.',
            titleTranslation: 'coupons',
            descriptionTranslation: 'coupons',
            actions: [{ id: 'ok', label: 'Okay' }],
            onSubmit: () => {},
          });
          return;
          }
        }
      }

      const ref = db.collection('requestsV03').doc(reservationId);

      // What the form shows; only fields that differ from when it opened are
      // written (see saveEditedReservation).
      const form: any = {
        time,
        startTimeInBlocks,
        endTimeInBlocks: startTimeInBlocks + reservationLength,
        guests,
        date,
        occassion,
        tables: tables || [],
        tableStr,
        space,
        reservationLength,
      };

      let data: any = {
        updatedAt: DateNow(),
        updatedBy: waiterSignature || actingAs(restaurant, updatedBy),
        sendConfirmationEmail,
      };

      [form, data].forEach((fields) =>
        Object.keys(fields).forEach((key) => {
          if (fields[key] === undefined) {
            delete fields[key];
          }
        })
      );

      if (editReservation.clearValidTill) {
        // Saving an expired hold must free the doc: the edit payload
        // otherwise never touches validTill and the row stays locked.
        data.validTill = 0;
      }

      const customWrites = customDataWrites(editReservation.customData);
      Object.assign(data, customWrites.patch);

      // Each field is written only when the mobile edit dialog touched it, so
      // a save never reverts a comment someone changed elsewhere meanwhile.
      if (editReservation.commentEdited && editReservation.guest) {
        data['guest.comment'] = editReservation.guest.comment ?? '';
      }

      if (editReservation.hostCommentEdited && editReservation.guest) {
        data['guest.hostComment'] = editReservation.guest.hostComment ?? '';
      }

      // The updated confirmation with the new amounts comes from the coupon
      // confirmation below; the plain change email would be a second one.
      if (couponDecision?.summary.evaluation?.financialChange) {
        data.sendConfirmationEmail = false;
      }

      try {
        await trackChange(
          grant,
          'reservation.edit',
          saveEditedReservation({
            id: ref.id,
            baseline: editReservation.baseline,
            form,
            extra: data,
            placement,
          }),
          {
            targets: [{ type: 'reservation', id: ref.id }],
            fromResult: (patch) => ({
              changes: reservationChanges(editReservation.baseline, patch),
            }),
          }
        );
      } catch (error) {
        if (changedMeanwhile(error)) return;
        throw error;
      }

      // The save above is committed: a failure here must not skip the steps
      // below or look like an unsaved booking.
      if (customWrites.literal.length) {
        try {
          await ref.update(...(customWrites.literal as [any, any, ...any[]]));
        } catch (error) {
          console.error('custom fields save', error);
          toast('Die Reservierung ist gespeichert, einige Zusatzfelder aber nicht. Bitte erneut eintragen.', Severity.ERROR);
        }
      }

      if (isTicketSessionMode(editReservation.ticketMode)) {
        await createTicketSession(undefined, !!couponDecision);
      }

      if (couponDecision && restaurant && reservationId) {
        try {
          await confirmCouponChange(restaurant, reservationId, couponDecision);
        } catch (error) {
          alert({
            title: 'Discount recalculation not recorded',
            description:
              'The booking is saved, but the new discount amounts were not recorded. Open the ticket and record the recalculation there.',
            titleTranslation: 'coupons',
            descriptionTranslation: 'coupons',
            actions: [{ id: 'ok', label: 'Okay' }],
            onSubmit: () => {},
          });
        }
      }

      trackAnalytics("dashboard_reservation_saved", {
          flow: "dashboard",
          restaurant_id: restaurant || undefined,
          outcome: "updated"
      });
      seteditReservation(null);

      if (currentDate === date) {
        setcurrentReservation(reservationId || null);
      }
    }
  };

  const handleSubmit = async (
    signatureOverride?: string,
    pinChecked?: boolean,
    grant?: SecurityGrant
  ): Promise<void> => {
    let sendConfirmationEmail = false;

    if (editReservation?.waitinglistEntry?.type === 'largeGroup' &&
        !hasConcreteTime(editReservation.timeSlot?.time)) {
      toast('Bitte wähle eine konkrete Uhrzeit, bevor du die Gruppenanfrage übernimmst.', Severity.WARNING, null);
      return;
    }

    console.log({ editReservation, settings });

    // onClick handlers may pass a SyntheticEvent as first arg — never
    // let a non-string reach Firestore (updatedBy/createdBy).
    const signature =
      typeof signatureOverride === 'string' ? signatureOverride : undefined;

    if (
      editReservation &&
      !editReservation.new &&
      !editReservation.waitinglistEntry &&
      pinChecked !== true
    ) {
      // Stays pending while the PIN dialog is open: the mobile Save button
      // locks on this promise.
      return new Promise<void>((resolve) =>
        authenticate(
          'reservations.editReservation.write',
          (pinSignature, grant) =>
            handleSubmit(pinSignature || signature, true, grant).finally(() =>
              resolve()
            ),
          () => resolve(),
          {
            action: 'reservation.edit',
            targets: editReservation.reservationId
              ? [{ type: 'reservation', id: editReservation.reservationId }]
              : [],
          }
        )
      );
    }

    const waiterSignature = signature ?? editReservation?.waiterSignature;

    if (
      (!!editReservation?.page || !editReservation?.new) &&
      settings?.signatureRequired &&
      !waiterSignature
    ) {
      setSignaturePromptOpen(true);

      return;
    }

    if (
      (!editReservation?.new || !!editReservation?.waitinglistEntry) &&
      editReservation?.guest?.email &&
      editReservation.sendConfirmationEmail
    ) {
      sendConfirmationEmail = true;
    }

    if (!editReservation?.timeSlot || !editReservation.guests || !restaurant) {
      // No TimeSlot Message
      alert({
        title: 'No Time slot was selected!',
        titleTranslation: 'reservations',
        description:
          "You can't continue without a time for the reservation to start",
        descriptionTranslation: 'reservations',
        onSubmit: () => {},
        actions: [
          {
            id: 'ok',
            label: 'Okay',
          },
        ],
      });

      return;
    }

    const saveStillOpen = beginEditSession();
    const onSaveError = (error: any) => {
      trackAnalytics("dashboard_reservation_error", {
          flow: "dashboard",
          restaurant_id: restaurant || undefined,
          error_category: analyticsErrorCategory(error)
      });
      // The finalize is marked before its writes, so a failed save has to give
      // the placeholder cleanup its session back.
      if (finalizedRef.current === editReservation?.reservationId) finalizedRef.current = null;
      if (editReservation?.visitWishId && editReservation.visitWishHoldCreated && editReservation.reservationId && !saveStillOpen()) {
        void abandonVisitWishHold({ restaurantId: restaurant!, wishId: editReservation.visitWishId, reservationId: editReservation.reservationId });
      }

      toast(
        error?.response?.data?.message ??
          error?.message ??
          'Reservierung konnte nicht gespeichert werden.',
        Severity.ERROR
      );
    };

    if (
      !isLightReservation &&
      !editReservation.tables?.length &&
      !editReservation.addToWaitinglist
    ) {
      // No Tables Modal

      alert({
        title: 'No Tables where selected!',
        titleTranslation: 'reservations',
        description: 'Are you sure you want to continue?',
        descriptionTranslation: 'reservations',
        onSubmit: () =>
          submitReservation(sendConfirmationEmail, waiterSignature, grant).catch(
            onSaveError
          ),
      });

      return;
    }

    try {
      await submitReservation(sendConfirmationEmail, waiterSignature, grant);
    } catch (error: any) {
      onSaveError(error);
    }
  };

  const handleSignatureConfirmed = (signature: string) => {
    if (!validateSignature(signature)) return;

    setSignaturePromptOpen(false);

    seteditReservation((current) =>
      current ? { ...current, waiterSignature: signature } : current
    );

    handleSubmit(signature);
  };

  const closeSignaturePrompt = () => setSignaturePromptOpen(false);

  useEffect(() => {
    if (editReservation) {
      setoriginalUrl(editReservation.originalUrl ?? null);
    } else if (originalUrl) {
      goBackToOriginal();
    }
  }, [editReservation]);

  /** The current editor session (see editorSessionRef). */
  const editorSession = () => editorSessionRef.current;

  /** Opens an editor from outside this hook (the waiting list) as a new session. */
  const openEditor: React.Dispatch<React.SetStateAction<EditReservation | null>> = (next) => {
    startEditorSession();
    seteditReservation(next);
  };

  return {
    editReservation,
    seteditReservation,
    beginEditSession,
    editorSession,
    openEditor,
    handleRelocate,
    handleSubmit,
    handleNewReservation,
    signaturePromptOpen,
    closeSignaturePrompt,
    handleSignatureConfirmed,
  };
};

export default useEditReservation;
